import React, { useMemo, useState } from "react";
import "./css/SpendingEvents.css";

import { ITEM_LABELS, SPENDING_EVENT_CONFIGS } from "./spending-events/rescueAlisaConfig";
function formatNumber(value) {
  return Number(value || 0).toLocaleString();
}

function formatUsdCents(value) {
  return (Number(value || 0) / 100).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
  });
}

function formatItem(item) {
  const levelLabel = item.level ? ` L${item.level}` : "";
  return `${formatNumber(item.count)}x ${ITEM_LABELS[item.gid] || "Unmapped reward"}${levelLabel}`;
}

function addRewardToMap(map, reward) {
  const key = `${reward.gid}:${reward.level || 0}`;
  const existing = map.get(key) || { ...reward, count: 0 };
  existing.count += reward.count || 0;
  map.set(key, existing);
}

function buildRewardTotals(levels) {
  const totals = new Map();
  (levels || []).forEach((level) => {
    (level.guaranteed || []).forEach((reward) => addRewardToMap(totals, reward));
    (level.bonus || []).forEach((reward) => addRewardToMap(totals, reward));
    (Array.isArray(level.free) ? level.free : []).forEach((reward) => addRewardToMap(totals, reward));
    (Array.isArray(level.premium) ? level.premium : []).forEach((reward) => addRewardToMap(totals, reward));
  });
  return [...totals.values()].sort((a, b) => {
    if (a.gid !== b.gid) return a.gid - b.gid;
    return (a.level || 0) - (b.level || 0);
  });
}

function formatRewardList(rewards) {
  return rewards?.length ? rewards.map(formatItem).join(", ") : "None";
}

function buildComplexRewardTotals(eventConfig) {
  const totals = new Map();
  const addMany = (rewards = []) => rewards.forEach((reward) => addRewardToMap(totals, reward));

  (eventConfig.dailyTasks || []).forEach((row) => addMany(row.rewards));
  (eventConfig.dropRewards || []).forEach((row) => addMany(row.rewards));
  (eventConfig.scoreMilestones || []).forEach((row) => addMany(row.rewards));
  (eventConfig.pointMilestones || []).forEach((row) => addMany(row.rewards));
  (eventConfig.exchangeStore || []).forEach((row) => addMany(row.rewards));
  (eventConfig.specialLadder || []).forEach((row) => addMany(row.rewards));
  (eventConfig.bossRewards || []).forEach((row) => addMany(row.rewards));
  (eventConfig.dailyScoreRewards || []).forEach((row) => addMany(row.rewards));
  (eventConfig.currencyPacks || []).forEach((row) => addMany(row.bonus));
  (eventConfig.upgradeLevels || []).forEach((row) => addMany(row.materials));
  (eventConfig.spendMilestones || []).forEach((row) => addMany(row.rewards));
  (eventConfig.rewardPools || []).forEach((row) => addMany(row.rewards));
  (eventConfig.bountyLadder || []).forEach((row) => {
    addMany(row.free);
    addMany(row.premium);
  });
  addMany(eventConfig.premiumShowcase);
  (eventConfig.rankingRewardGroups || []).forEach((group) => {
    (group.rows || []).forEach((row) => addMany(row.rewards));
  });
  (eventConfig.handbook || []).forEach((row) => {
    addMany(row.rewards);
    addMany(row.alternateRewards);
  });
  addMany(eventConfig.handbookGuaranteedPool);
  (eventConfig.rankingRewards || []).forEach((row) => addMany(row.rewards));

  return [...totals.values()].sort((a, b) => {
    if (a.gid !== b.gid) return a.gid - b.gid;
    return (a.level || 0) - (b.level || 0);
  });
}

function formatTaskDescription(task) {
  return task.description
    .replace("$0", formatNumber(task.target))
    .replace("$1", `0/${formatNumber(task.target)}`);
}

function formatTemplateRequirement(template, requirement) {
  return String(template || "")
    .replace("$0", formatNumber(requirement))
    .replace("$1", `0/${formatNumber(requirement)}`);
}

function formatDuration(seconds) {
  const totalSeconds = Number(seconds || 0);
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) return "";
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  if (hours && minutes) return `${hours}h ${minutes}m`;
  if (hours) return `${hours}h`;
  if (minutes) return `${minutes}m`;
  return `${totalSeconds}s`;
}

function formatRankRange(row) {
  return row.from === row.to ? `Rank ${row.from}` : `Ranks ${row.from}-${row.to}`;
}

function getPackCurrencyAmount(pack) {
  return Number(pack.cookies ?? pack.amount ?? pack.currencyAmount ?? 0);
}

function isBetterPlan(candidate, current) {
  if (!current) return true;
  if (candidate.costCents !== current.costCents) return candidate.costCents < current.costCents;
  return candidate.cookies < current.cookies;
}

const EMPTY_CURRENCY_PLAN = { costCents: 0, cookies: 0, counts: {} };

function getPackPurchaseLimit(pack) {
  return Number(pack.limit || pack.maxPurchases || 999);
}

function buildChainedPackOptionGroups(packs) {
  const optionGroups = [];
  const orderedPacks = (packs || []).filter((pack) => getPackCurrencyAmount(pack) > 0);
  const maxCopies = Math.max(0, ...orderedPacks.map(getPackPurchaseLimit));

  for (let copy = 1; copy <= maxCopies; copy += 1) {
    let amount = 0;
    let costCents = 0;
    const counts = {};
    const options = [];

    orderedPacks.forEach((pack) => {
      if (getPackPurchaseLimit(pack) < copy) return;
      amount += getPackCurrencyAmount(pack);
      costCents += Number(pack.priceCents || 0);
      counts[pack.id] = 1;
      options.push({
        id: `${pack.id}:chain:${copy}`,
        amount,
        costCents,
        counts: { ...counts },
      });
    });

    if (options.length) optionGroups.push(options);
  }

  return optionGroups;
}

function buildCurrencyPlans(packs, targetCurrency = null) {
  const packOptionGroups = buildChainedPackOptionGroups(packs);
  const packOptions = packOptionGroups.flat();
  const optionAmounts = packOptions.map((option) => option.amount);
  const largestPack = Math.max(0, ...optionAmounts);
  const boundedTarget = Number.isFinite(targetCurrency) && targetCurrency !== null
    ? Math.max(0, Number(targetCurrency || 0) + largestPack)
    : null;
  const maxCookies = boundedTarget ?? packOptionGroups.reduce((sum, options) => {
    const largestOption = options[options.length - 1];
    return sum + (largestOption?.amount || 0);
  }, 0);
  const dp = Array(maxCookies + 1).fill(null);
  dp[0] = { costCents: 0, cookies: 0, counts: {} };

  packOptionGroups.forEach((options) => {
    const nextDp = dp.slice();

    for (let cookies = 0; cookies <= maxCookies; cookies += 1) {
      const current = dp[cookies];
      if (!current) continue;

      options.forEach((option) => {
        const amount = option.amount;
        if (!amount) return;
        const nextCookies = cookies + amount;
        if (nextCookies > maxCookies) return;

        const nextCounts = { ...current.counts };
        Object.entries(option.counts).forEach(([packId, count]) => {
          nextCounts[packId] = (nextCounts[packId] || 0) + count;
        });
        const candidate = {
          costCents: current.costCents + option.costCents,
          cookies: nextCookies,
          counts: nextCounts,
        };

        if (isBetterPlan(candidate, nextDp[nextCookies])) {
          nextDp[nextCookies] = candidate;
        }
      });
    }

    for (let i = 0; i <= maxCookies; i += 1) dp[i] = nextDp[i];
  });

  return {
    maxCurrency: maxCookies,
    getBestPlan(targetCookies) {
      if (targetCookies <= 0) return dp[0] || EMPTY_CURRENCY_PLAN;
      let best = null;
      for (let cookies = Math.max(0, targetCookies); cookies <= maxCookies; cookies += 1) {
        const candidate = dp[cookies];
        if (candidate && isBetterPlan(candidate, best)) best = candidate;
      }
      return best;
    },
  };
}

function buildCookiePlans(packs) {
  return buildCurrencyPlans(packs);
}

function getCurrencyRewardCount(level, currencyItemGid) {
  if (!currencyItemGid) return 0;
  return [...(level.guaranteed || []), ...(level.bonus || [])].reduce((sum, reward) => (
    reward.gid === currencyItemGid ? sum + (reward.count || 0) : sum
  ), 0);
}

function buildLadderRows(levels, packs, currencyItemGid) {
  const plans = buildCookiePlans(packs);
  let cumulativeCookies = 0;
  let cumulativeRewardCookies = 0;
  let previousCostCents = 0;
  let paidLevel = 0;

  return levels.map((level) => {
    cumulativeCookies += level.requiredCookies;
    if (!level.free) paidLevel += 1;
    const purchaseTargetCookies = Math.max(0, cumulativeCookies - cumulativeRewardCookies);
    const plan = plans.getBestPlan(purchaseTargetCookies) || EMPTY_CURRENCY_PLAN;
    const rewardCookies = getCurrencyRewardCount(level, currencyItemGid);
    const row = {
      ...level,
      displayLabel: level.free ? "Free" : `Level ${level.displayLevel || level.sLevel || paidLevel}`,
      cumulativeCookies,
      purchaseTargetCookies,
      rewardCookies,
      cumulativeRewardCookies: cumulativeRewardCookies + rewardCookies,
      deliveredCookies: plan.cookies,
      overageCookies: Math.max(0, plan.cookies - purchaseTargetCookies),
      costCents: plan.costCents,
      incrementalCostCents: Math.max(0, plan.costCents - previousCostCents),
      costPerPurchasedCookie: purchaseTargetCookies > 0 ? plan.costCents / 100 / purchaseTargetCookies : 0,
      planCounts: plan.counts,
    };
    previousCostCents = plan.costCents;
    cumulativeRewardCookies += rewardCookies;
    return row;
  });
}

function buildSpendMilestoneRows(milestones, packs) {
  const knownTargets = (milestones || [])
    .map((row) => Number(row.target))
    .filter((target) => Number.isFinite(target) && target > 0);
  const maxTarget = Math.max(0, ...knownTargets);
  const plans = buildCurrencyPlans(packs, maxTarget);
  let previousCostCents = 0;

  return (milestones || []).map((row) => {
    const target = Number(row.target);
    const hasKnownCurrencyCost = Number.isFinite(target) && target > 0;
    if (!hasKnownCurrencyCost) {
      return {
        ...row,
        hasKnownCurrencyCost: false,
      };
    }
    const plan = plans.getBestPlan(target);
    if (!plan) {
      return {
        ...row,
        hasKnownCurrencyCost: true,
        isReachable: false,
      };
    }
    const nextRow = {
      ...row,
      hasKnownCurrencyCost: true,
      isReachable: true,
      deliveredCurrency: plan.cookies,
      overageCurrency: Math.max(0, plan.cookies - target),
      costCents: plan.costCents,
      incrementalCostCents: Math.max(0, plan.costCents - previousCostCents),
      costPerPurchasedCurrency: target > 0 ? plan.costCents / 100 / target : 0,
      planCounts: plan.counts,
    };
    previousCostCents = plan.costCents;
    return nextRow;
  });
}

function buildUpgradeLevelRows(levels, packs) {
  const knownTargets = (levels || [])
    .map((row) => Number(row.points))
    .filter((target) => Number.isFinite(target) && target > 0);
  const maxTarget = Math.max(0, ...knownTargets);
  const plans = buildCurrencyPlans(packs, maxTarget);
  let previousCostCents = 0;

  return (levels || []).map((row) => {
    const target = Number(row.points);
    const hasKnownCurrencyCost = Number.isFinite(target) && target >= 0;
    if (!hasKnownCurrencyCost) {
      return {
        ...row,
        hasKnownCurrencyCost: false,
      };
    }

    const plan = plans.getBestPlan(target);
    if (!plan) {
      return {
        ...row,
        hasKnownCurrencyCost: true,
        isReachable: false,
      };
    }

    const nextRow = {
      ...row,
      hasKnownCurrencyCost: true,
      isReachable: true,
      deliveredCurrency: plan.cookies,
      overageCurrency: Math.max(0, plan.cookies - target),
      costCents: plan.costCents,
      incrementalCostCents: Math.max(0, plan.costCents - previousCostCents),
      costPerPurchasedCurrency: target > 0 ? plan.costCents / 100 / target : 0,
      planCounts: plan.counts,
    };
    previousCostCents = plan.costCents;
    return nextRow;
  });
}

function planLabel(planCounts, packs) {
  const parts = packs
    .map((pack) => {
      const count = planCounts?.[pack.id] || 0;
      const label = pack.label || formatNumber(getPackCurrencyAmount(pack));
      return count > 0 ? `${count}x ${label}` : null;
    })
    .filter(Boolean);
  return parts.length ? parts.join(", ") : "No purchase";
}

function buildBattlePassRows(month) {
  const passCostCents = Number(month?.premiumPriceCents || 0);
  return (month?.levels || []).map((level) => ({
    ...level,
    cumulativeCostCents: passCostCents,
    costPerLevel: level.level > 0 && passCostCents > 0 ? passCostCents / level.level : 0,
    totalRewardCount: [...(level.free || []), ...(level.premium || [])]
      .reduce((sum, reward) => sum + Number(reward.count || 0), 0),
  }));
}

function ComplexSpendingEvent({ eventConfig }) {
  const spendMilestoneRows = useMemo(() => {
    if (!["currency-ladder", "partial-currency-ladder"].includes(eventConfig.spendMilestoneMode)) {
      return eventConfig.spendMilestones || [];
    }
    return buildSpendMilestoneRows(eventConfig.spendMilestones || [], eventConfig.currencyPacks || []);
  }, [eventConfig]);
  const upgradeLevelRows = useMemo(() => {
    if (!eventConfig.upgradeCurrencyPacks?.length) return eventConfig.upgradeLevels || [];
    return buildUpgradeLevelRows(eventConfig.upgradeLevels || [], eventConfig.upgradeCurrencyPacks || []);
  }, [eventConfig]);

  return (
    <>
      {eventConfig.currencyPacks?.length && eventConfig.showCurrencyPacks !== false ? (
        <section className="spending-events-section">
          <div className="spending-events-section-head">
            <h2>{eventConfig.currencyPackTitle || `${eventConfig.currencyLabel || "Currency"} Packs`}</h2>
            <span>{eventConfig.currencyPackDescription || "Purchasable event currency tiers"}</span>
          </div>
          <div className="cookie-pack-grid marble-pack-grid">
            {eventConfig.currencyPacks.map((pack) => (
              <article className="cookie-pack" key={pack.id}>
                <strong>{formatNumber(pack.amount)}</strong>
                <span>{eventConfig.currencyLabel || "currency"}</span>
                <div>{formatUsdCents(pack.priceCents)}</div>
                {pack.bonus?.length ? <small>Bonus: {formatRewardList(pack.bonus)}</small> : null}
              </article>
            ))}
          </div>
        </section>
      ) : null}

      {eventConfig.marblePacks?.length ? (
        <section className="spending-events-section">
          <div className="spending-events-section-head">
            <h2>Marble Packs</h2>
            <span>Marbles received per purchase tier</span>
          </div>
          <div className="cookie-pack-grid marble-pack-grid">
            {eventConfig.marblePacks.map((pack) => (
              <article className="cookie-pack" key={pack.id}>
                <strong>{formatNumber(pack.marbles)}</strong>
                <span>marbles</span>
                <div>{formatUsdCents(pack.priceCents)}</div>
              </article>
            ))}
          </div>
        </section>
      ) : null}

      {eventConfig.supplyPacks?.length ? (
        <section className="spending-events-section">
          <div className="spending-events-section-head">
            <h2>{eventConfig.supplyPackTitle || "Event Supplies"}</h2>
            <span>{eventConfig.supplyPackDescription || "Event supply entries"}</span>
          </div>
          <div className="complex-card-grid">
            {eventConfig.supplyPacks.map((pack) => (
              <article className="complex-card" key={pack.id}>
                <span>Tier {pack.tier}</span>
                <strong>{pack.label || ITEM_LABELS[pack.gid] || "Event supplies"}</strong>
                {pack.contents?.length ? <p>{formatRewardList(pack.contents)}</p> : null}
              </article>
            ))}
          </div>
        </section>
      ) : null}

      {eventConfig.upgradeCurrencyPacks?.length ? (
        <section className="spending-events-section">
          <div className="spending-events-section-head">
            <h2>{eventConfig.upgradeCurrencyPackTitle || `${eventConfig.upgradeCurrencyLabel || "Upgrade Currency"} Packs`}</h2>
            <span>{eventConfig.upgradeCurrencyPackDescription || "Upgrade currency purchase tiers"}</span>
          </div>
          <div className="cookie-pack-grid marble-pack-grid">
            {eventConfig.upgradeCurrencyPacks.map((pack) => (
              <article className="cookie-pack" key={pack.id}>
                <strong>{formatNumber(getPackCurrencyAmount(pack))}</strong>
                <span>{eventConfig.upgradeCurrencyLabel || "currency"}</span>
                <div>{formatUsdCents(pack.priceCents)}</div>
                <small>
                  {pack.dailyLimit ? `${pack.dailyLimit}/day` : "Limited"}
                  {pack.limit ? `, ${pack.limit} total` : ""}
                </small>
              </article>
            ))}
          </div>
        </section>
      ) : null}

      {eventConfig.upgradeLevels?.length ? (
        <section className="spending-events-section">
          <div className="spending-events-section-head">
            <h2>{eventConfig.upgradeLadderTitle || "Upgrade Ladder"}</h2>
            <span>{eventConfig.upgradeLadderDescription || "Required materials by level"}</span>
          </div>
          <div className="upgrade-level-grid">
            {upgradeLevelRows.map((row) => (
              <article className="upgrade-level-card" key={row.level}>
                <div>
                  <span>Level {row.level}</span>
                  <strong>{formatNumber(row.points)} {eventConfig.upgradeCurrencyLabel || "pts"}</strong>
                </div>
                {row.hasKnownCurrencyCost ? (
                  <div className="upgrade-spend-plan">
                    <span>Best spend</span>
                    <strong>{row.isReachable ? formatUsdCents(row.costCents) : "Not reachable"}</strong>
                    {row.isReachable ? (
                      <>
                        <small>Incremental: {formatUsdCents(row.incrementalCostCents)}</small>
                        <small>
                          Delivered {formatNumber(row.deliveredCurrency)}
                          {row.overageCurrency ? ` (+${formatNumber(row.overageCurrency)})` : ""}
                        </small>
                        <small>{planLabel(row.planCounts, eventConfig.upgradeCurrencyPacks || [])}</small>
                      </>
                    ) : (
                      <small>5-day pack limits do not reach this level.</small>
                    )}
                  </div>
                ) : null}
                <p>{formatRewardList(row.materials)}</p>
                {row.speedMultiplier ? <small>{row.speedMultiplier.toFixed(1)}x speed</small> : null}
              </article>
            ))}
          </div>
        </section>
      ) : null}

      {eventConfig.giftItems?.length ? (
        <section className="spending-events-section">
          <div className="spending-events-section-head">
            <h2>{eventConfig.giftItemsTitle || "Gift Shop"}</h2>
            <span>{eventConfig.giftItemsDescription || "Gift costs and score value"}</span>
          </div>
          <div className="complex-card-grid">
            {eventConfig.giftItems.map((row) => (
              <article className={`complex-card ${row.featured ? "is-highlighted" : ""}`} key={row.id}>
                <span>{row.label}</span>
                <strong>{formatNumber(row.charm)} charm</strong>
                <p>{formatNumber(row.cost)}x {ITEM_LABELS[row.costItemGid] || "event currency"}</p>
              </article>
            ))}
          </div>
        </section>
      ) : null}

      {eventConfig.specialLadder?.length ? (
        <section className="spending-events-section">
          <div className="spending-events-section-head">
            <h2>{eventConfig.specialLadderTitle || "Reward Ladder"}</h2>
            <span>{eventConfig.specialLadderDescription || "Step rewards"}</span>
          </div>
          <div className="complex-card-grid">
            {eventConfig.specialLadder.map((row) => (
              <article className={`complex-card ${row.specialReward ? "is-highlighted" : ""}`} key={row.id}>
                <span>Step {row.id}{row.specialReward ? " · prize" : ""}</span>
                <strong>{formatRewardList(row.rewards)}</strong>
              </article>
            ))}
          </div>
        </section>
      ) : null}

      {eventConfig.discountLadders?.length ? (
        <section className="spending-events-section">
          <div className="spending-events-section-head">
            <h2>Discount Ladders</h2>
            <span>Group thresholds and unlocked purchase price</span>
          </div>
          <div className="complex-two-column">
            {eventConfig.discountLadders.map((ladder) => (
              <div className="reward-panel" key={ladder.id}>
                <h3>{ladder.label}</h3>
                {ladder.items?.length ? (
                  <div className="discount-pack-items">
                    <span>Pack items</span>
                    <strong>{formatRewardList(ladder.items)}</strong>
                  </div>
                ) : null}
                <div className="complex-row-list compact">
                  {ladder.rows.map((row) => (
                    <article className="complex-row discount-row" key={row.id}>
                      <div>
                        <strong>{formatUsdCents(row.priceCents)}</strong>
                        <span>{row.offPercent ? `${row.offPercent}% off` : "Base price"}</span>
                      </div>
                      <p>
                        {row.thresholdLabel || `${formatNumber(row.threshold)} points`}
                        {row.thresholdDetail ? <span>{row.thresholdDetail}</span> : null}
                      </p>
                    </article>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {eventConfig.spendMilestones?.length || eventConfig.rewardPools?.length ? (
        <section className="spending-events-section">
          <div className="spending-events-section-head">
            <h2>{eventConfig.spendMilestoneTitle || "Spend Rewards"}</h2>
            <span>{eventConfig.spendMilestoneDescription || "Milestones and shared reward pools"}</span>
          </div>
          {["currency-ladder", "partial-currency-ladder"].includes(eventConfig.spendMilestoneMode) ? (
            <div className="spending-ladder compact-ladder">
              {spendMilestoneRows.map((row) => (
                <article className={`ladder-tier ${row.featured ? "is-highlighted" : ""}`} key={row.id}>
                  <div className="ladder-tier-top">
                    <div>
                      <span className="ladder-level">
                        {row.targetLabel || `${formatNumber(row.target)} ${eventConfig.spendMilestoneUnit || "spend"}`}
                      </span>
                      <strong>
                        {row.hasKnownCurrencyCost
                          ? (row.isReachable === false ? "Not reachable" : formatUsdCents(row.costCents))
                          : "Unknown"}
                      </strong>
                    </div>
                    <span className={`ladder-pill ${row.hasKnownCurrencyCost ? "is-paid" : "is-unknown"}`}>
                      {row.targetPillLabel || (row.hasKnownCurrencyCost ? `${formatNumber(row.target)} XP` : "Cost unknown")}
                    </span>
                  </div>

                  <div className="ladder-stats">
                    {row.scoreTarget ? (
                      <div>
                        <span>Event score required</span>
                        <strong>{formatNumber(row.scoreTarget)}</strong>
                      </div>
                    ) : null}
                    <div>
                      <span>Cumulative XP</span>
                      <strong>{row.hasKnownCurrencyCost ? formatNumber(row.target) : "Unknown"}</strong>
                    </div>
                    {row.hasKnownCurrencyCost ? (
                      <>
                        <div>
                          <span>Incremental dollars</span>
                          <strong>{row.isReachable === false ? "N/A" : formatUsdCents(row.incrementalCostCents)}</strong>
                        </div>
                        <div>
                          <span>Delivered {eventConfig.currencyLabel || "currency"}</span>
                          <strong>{row.isReachable === false ? "N/A" : formatNumber(row.deliveredCurrency)}</strong>
                        </div>
                        <div>
                          <span>Cost / bought {eventConfig.currencyLabel || "currency"}</span>
                          <strong>{row.target && row.isReachable !== false ? `$${row.costPerPurchasedCurrency.toFixed(3)}` : "N/A"}</strong>
                        </div>
                      </>
                    ) : null}
                  </div>

                  {row.hasKnownCurrencyCost && row.isReachable !== false ? (
                    <div className="ladder-plan">
                      <span>Most efficient spending plan</span>
                      <strong>{planLabel(row.planCounts, eventConfig.currencyPacks || [])}</strong>
                    </div>
                  ) : row.hasKnownCurrencyCost ? (
                    <div className="ladder-plan">
                      <span>Most efficient spending plan</span>
                      <strong>Not reachable with pack limits</strong>
                    </div>
                  ) : null}

                  <div className="ladder-rewards">
                    <div>
                      <span>Rewards</span>
                      <p>{formatRewardList(row.rewards)}</p>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="complex-two-column">
            {eventConfig.spendMilestones?.length ? (
              <div className="reward-panel">
                <h3>Milestones</h3>
                <div className="complex-row-list compact">
                  {spendMilestoneRows.map((row) => (
                    <article className={`complex-row ${row.featured ? "is-highlighted" : ""}`} key={row.id}>
                      <div>
                        <strong>
                          {row.targetLabel || `${formatNumber(row.target)} ${eventConfig.spendMilestoneUnit || "spend"}`}
                        </strong>
                        {row.targetDetail ? <span>{row.targetDetail}</span> : null}
                      </div>
                      <p>{formatRewardList(row.rewards)}</p>
                    </article>
                  ))}
                </div>
              </div>
            ) : null}
            {eventConfig.rewardPools?.length ? (
              <div className="reward-panel">
                <h3>Reward Pool</h3>
                <div className="complex-row-list compact">
                  {eventConfig.rewardPools.map((row) => (
                    <article className="complex-row" key={row.id}>
                      <div>
                        <strong>{row.label || `Pool ${row.id}`}</strong>
                      </div>
                      <p>{formatRewardList(row.rewards)}</p>
                    </article>
                  ))}
                </div>
              </div>
            ) : null}
            </div>
          )}
        </section>
      ) : null}

      {eventConfig.bountyTasks?.length ? (
        <section className="spending-events-section">
          <div className="spending-events-section-head">
            <h2>{eventConfig.bountyTasksTitle || "Bounties"}</h2>
            <span>{eventConfig.bountyTasksDescription || "Available bounty objectives and point values"}</span>
          </div>
          <div className="bounty-task-grid">
            {eventConfig.bountyTasks.map((task) => (
              <article className={`bounty-task-card bounty-star-${task.star}`} key={task.id}>
                <div className="bounty-task-top">
                  <span>{"★".repeat(Math.max(1, Number(task.star || 1)))}</span>
                  <strong>{formatNumber(task.points)} pts</strong>
                </div>
                <h3>{task.title}</h3>
                <p>{formatTemplateRequirement(task.content, task.requirement)}</p>
                <div className="bounty-task-meta">
                  <span>{formatDuration(task.timeSeconds)}</span>
                  <span>{task.type}</span>
                </div>
              </article>
            ))}
          </div>
        </section>
      ) : null}

      {eventConfig.bountyLadder?.length ? (
        <section className="spending-events-section">
          <div className="spending-events-section-head">
            <h2>{eventConfig.bountyLadderTitle || "Bounty Ladder"}</h2>
            <span>{eventConfig.bountyLadderDescription || "Free and premium rewards by point level"}</span>
          </div>
          <div className="spending-ladder bounty-ladder">
            {eventConfig.bountyLadder.map((row) => (
              <article className="ladder-tier bounty-ladder-tier" key={row.level}>
                <div className="ladder-tier-top">
                  <div>
                    <span className="ladder-level">{`Level ${row.level}`}</span>
                    <strong>{formatNumber(row.points)} pts</strong>
                  </div>
                  <span className="ladder-pill is-paid">Premium</span>
                </div>
                <div className="ladder-rewards battle-pass-rewards">
                  <div>
                    <span>Free</span>
                    <p>{formatRewardList(row.free)}</p>
                  </div>
                  <div>
                    <span>Premium</span>
                    <p>{formatRewardList(row.premium)}</p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>
      ) : null}

      {eventConfig.premiumShowcase?.length ? (
        <section className="spending-events-section">
          <div className="spending-events-section-head">
            <h2>{eventConfig.premiumShowcaseTitle || "Premium Preview"}</h2>
            <span>Preview rewards for the premium track.</span>
          </div>
          <div className="complex-callout">
            <span>Premium rewards shown in event panel</span>
            <p>{formatRewardList(eventConfig.premiumShowcase)}</p>
          </div>
        </section>
      ) : null}

      {eventConfig.dailyTasks?.length ? (
        <section className="spending-events-section">
          <div className="spending-events-section-head">
            <h2>Daily Quests</h2>
            <span>Daily objectives, score, and rewards</span>
          </div>
          <div className="complex-row-list">
            {eventConfig.dailyTasks.map((task) => (
              <article className="complex-row" key={task.id}>
                <div>
                  <strong>{formatTaskDescription(task)}</strong>
                  <span>{formatNumber(task.score)} handbook points</span>
                </div>
                <p>{formatRewardList(task.rewards)}</p>
              </article>
            ))}
          </div>
        </section>
      ) : null}

      {eventConfig.dropSlots?.length || eventConfig.dropRewards?.length ? (
        <section className="spending-events-section">
          <div className="spending-events-section-head">
            <h2>Marble Drop Board</h2>
            <span>Slot score values and drop rewards</span>
          </div>
          {eventConfig.dropSlots?.length ? (
            <div className="drop-slot-grid">
              {eventConfig.dropSlots.map((slot) => (
                <div className="drop-slot" key={slot.slot}>
                  <span>Slot {slot.slot}</span>
                  <strong>{formatNumber(slot.points)}</strong>
                </div>
              ))}
            </div>
          ) : null}
          <div className="complex-card-grid">
            {(eventConfig.dropRewards || []).map((row) => (
              <article className="complex-card" key={row.id}>
                <span>{row.board}</span>
                <strong>{formatRewardList(row.rewards)}</strong>
              </article>
            ))}
          </div>
        </section>
      ) : null}

      {eventConfig.bossRewards?.length ? (
        <section className="spending-events-section">
          <div className="spending-events-section-head">
            <h2>{eventConfig.bossRewardTitle || "Boss Rewards"}</h2>
            <span>Reward rates and per-event caps</span>
          </div>
          <div className="complex-card-grid">
            {eventConfig.bossRewards.map((row) => (
              <article className={`complex-card ${row.rare ? "is-highlighted" : ""}`} key={row.id}>
                <span>{row.boss}{row.max ? ` · max ${formatNumber(row.max)}` : ""}</span>
                <strong>{formatRewardList(row.rewards)}</strong>
                <p>{row.rate ? `${row.rate.toFixed(2)}% drop rate` : "Special reward"}</p>
              </article>
            ))}
          </div>
        </section>
      ) : null}

      {eventConfig.scoreMilestones?.length || eventConfig.pointMilestones?.length || eventConfig.dailyScoreRewards?.length ? (
        <section className="spending-events-section">
          <div className="spending-events-section-head">
            <h2>Score Rewards</h2>
            <span>Daily score, drop score, and cumulative point milestones</span>
          </div>
          <div className="complex-two-column">
            {eventConfig.dailyScoreRewards?.length ? (
              <div className="reward-panel">
                <h3>Daily Score Prize</h3>
                <div className="complex-row-list compact">
                  {eventConfig.dailyScoreRewards.map((row) => (
                    <article className="complex-row" key={row.id}>
                      <div>
                        <strong>{formatNumber(row.score)} score</strong>
                      </div>
                      <p>{formatRewardList(row.rewards)}</p>
                    </article>
                  ))}
                </div>
              </div>
            ) : null}
            {eventConfig.scoreMilestones?.length ? (
              <div className="reward-panel">
                <h3>Drop Score Milestones</h3>
                <div className="complex-row-list compact">
                  {eventConfig.scoreMilestones.map((row) => (
                    <article className="complex-row" key={row.id}>
                      <div>
                        <strong>{formatNumber(row.score)} score</strong>
                        {row.isNew ? <span>New</span> : null}
                      </div>
                      <p>{formatRewardList(row.rewards)}</p>
                    </article>
                  ))}
                </div>
              </div>
            ) : null}
            {eventConfig.pointMilestones?.length ? (
              <div className="reward-panel">
                <h3>Point Milestones</h3>
                <div className="complex-row-list compact">
                  {eventConfig.pointMilestones.map((row) => (
                    <article className="complex-row" key={row.id}>
                      <div>
                        <strong>{formatNumber(row.points)} points</strong>
                        {row.isNew ? <span>New</span> : null}
                      </div>
                      <p>{formatRewardList(row.rewards)}</p>
                    </article>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </section>
      ) : null}

      {eventConfig.handbook?.length ? (
      <section className="spending-events-section">
        <div className="spending-events-section-head">
          <h2>Marble Handbook</h2>
          <span>Progress rewards and selectable/alternate rewards</span>
        </div>
        <div className="complex-card-grid">
          {(eventConfig.handbook || []).map((row) => (
            <article className="complex-card" key={row.points}>
              <span>{formatNumber(row.points)} points</span>
              <strong>{formatRewardList(row.rewards)}</strong>
              {row.alternateRewards?.length ? <p>{formatRewardList(row.alternateRewards)}</p> : null}
            </article>
          ))}
        </div>
        {eventConfig.handbookGuaranteedPool?.length ? (
          <div className="complex-callout">
            <span>Guaranteed pool thresholds</span>
            <strong>{(eventConfig.handbookGuaranteedThresholds || []).map(formatNumber).join(", ")}</strong>
            <p>{formatRewardList(eventConfig.handbookGuaranteedPool)}</p>
          </div>
        ) : null}
      </section>
      ) : null}

      {eventConfig.exchangeStore?.length ? (
      <section className="spending-events-section">
        <div className="spending-events-section-head">
          <h2>Exchange Store</h2>
          <span>Medal costs, purchase limits, and rewards</span>
        </div>
        <div className="complex-row-list">
          {(eventConfig.exchangeStore || []).map((row) => (
            <article className="complex-row exchange-row" key={row.id}>
              <div>
                <strong>{formatRewardList(row.rewards)}</strong>
                <span>Limit {formatNumber(row.limit)}{row.isNew ? " · New" : ""}</span>
              </div>
              <p>{formatRewardList(row.cost)}</p>
            </article>
          ))}
        </div>
      </section>
      ) : null}

      {eventConfig.rankingRewards?.length ? (
      <section className="spending-events-section">
        <div className="spending-events-section-head">
          <h2>Ranking Rewards</h2>
          <span>Top 100 event ranking brackets</span>
        </div>
        <div className="complex-card-grid">
          {(eventConfig.rankingRewards || []).map((row) => (
            <article className="complex-card" key={row.id}>
              <span>{formatRankRange(row)}</span>
              <strong>{formatRewardList(row.rewards)}</strong>
            </article>
          ))}
        </div>
      </section>
      ) : null}

      {eventConfig.rankingRewardGroups?.length ? (
      <section className="spending-events-section">
        <div className="spending-events-section-head">
          <h2>Ranking Rewards</h2>
          <span>Ranking brackets by ranking type</span>
        </div>
        <div className="complex-two-column">
          {eventConfig.rankingRewardGroups.map((group) => (
            <div className="reward-panel" key={group.id}>
              <h3>{group.label}</h3>
              <div className="complex-row-list compact">
                {(group.rows || []).map((row) => (
                  <article className="complex-row" key={row.id}>
                    <div>
                      <strong>{formatRankRange(row)}</strong>
                    </div>
                    <p>{formatRewardList(row.rewards)}</p>
                  </article>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
      ) : null}
    </>
  );
}

function SpendingEvents() {
  const [selectedEventId, setSelectedEventId] = useState(SPENDING_EVENT_CONFIGS[0]?.id || "");
  const eventConfig = useMemo(() => (
    SPENDING_EVENT_CONFIGS.find((event) => event.id === selectedEventId) || SPENDING_EVENT_CONFIGS[0]
  ), [selectedEventId]);
  const [selectedVariantIdByEvent, setSelectedVariantIdByEvent] = useState({});
  const selectedVariant = useMemo(() => {
    const variants = Array.isArray(eventConfig?.variants) ? eventConfig.variants : [];
    if (!variants.length) return null;
    const selectedVariantId = selectedVariantIdByEvent[eventConfig.id] || variants[0]?.id;
    return variants.find((variant) => variant.id === selectedVariantId) || variants[0];
  }, [eventConfig, selectedVariantIdByEvent]);
  const ladderRows = useMemo(() => (
    eventConfig.type === "currency-ladder"
      ? buildLadderRows(eventConfig.levels, eventConfig.cookiePacks, eventConfig.currencyItemGid)
      : []
  ), [eventConfig]);
  const battlePassRows = useMemo(() => (
    eventConfig.type === "battle-pass" ? buildBattlePassRows(selectedVariant) : []
  ), [eventConfig, selectedVariant]);
  const rewardTotals = useMemo(() => {
    if (eventConfig.type === "complex-event") return buildComplexRewardTotals(eventConfig);
    return buildRewardTotals(eventConfig.type === "battle-pass" ? battlePassRows : eventConfig.levels);
  }, [battlePassRows, eventConfig]);

  return (
    <main className="spending-events-page">
      <section className="spending-events-toolbar">
        <div>
          <p className="spending-events-kicker">Tools / Spending Events</p>
          <h1>{eventConfig.name}</h1>
        </div>
        <label className="spending-events-select">
          <span>Event</span>
          <select value={eventConfig.id} onChange={(event) => setSelectedEventId(event.target.value)}>
            {SPENDING_EVENT_CONFIGS.map((event) => (
              <option key={event.id} value={event.id}>{event.name}</option>
            ))}
          </select>
        </label>
        {Array.isArray(eventConfig.variants) && eventConfig.variants.length > 0 ? (
          <label className="spending-events-select">
            <span>{eventConfig.variantsLabel || "Variant"}</span>
            <select
              value={selectedVariant?.id || ""}
              onChange={(event) => setSelectedVariantIdByEvent((prev) => ({
                ...prev,
                [eventConfig.id]: event.target.value,
              }))}
            >
              {eventConfig.variants.map((variant) => (
                <option key={variant.id} value={variant.id}>{variant.label}</option>
              ))}
            </select>
          </label>
        ) : null}
      </section>

      {eventConfig.notes?.length > 0 && (
        <section className="spending-event-notes" aria-label="Event notes">
          {eventConfig.notes.map((note) => (
            <div className="spending-event-note" key={note.label}>
              <span>{note.label}</span>
              <strong>{note.value}</strong>
            </div>
          ))}
        </section>
      )}

      {eventConfig.type === "currency-ladder" ? (
        <>
          <section className="spending-events-section">
            <div className="spending-events-section-head">
              <h2>Ladder</h2>
              <span>Cumulative cookies and cheapest dollars</span>
            </div>
            <div className="spending-ladder">
              {ladderRows.map((row) => (
                <article className="ladder-tier" key={row.level}>
                  <div className="ladder-tier-top">
                    <div>
                      <span className="ladder-level">{row.displayLabel}</span>
                      <strong>{formatUsdCents(row.costCents)}</strong>
                    </div>
                    <span className={`ladder-pill ${row.free ? "is-free" : "is-paid"}`}>
                      {row.free ? "No cookies" : `+${formatNumber(row.requiredCookies)}`}
                    </span>
                  </div>

                  <div className="ladder-stats">
                    <div>
                      <span>Total ladder cookies</span>
                      <strong>{formatNumber(row.cumulativeCookies)}</strong>
                    </div>
                    <div>
                      <span>Reward cookies</span>
                      <strong>{formatNumber(row.cumulativeRewardCookies)}</strong>
                    </div>
                    <div>
                      <span>Cookies to buy</span>
                      <strong>{formatNumber(row.purchaseTargetCookies)}</strong>
                    </div>
                    <div>
                      <span>Incremental dollars</span>
                      <strong>{formatUsdCents(row.incrementalCostCents)}</strong>
                    </div>
                    <div>
                      <span>Delivered cookies</span>
                      <strong>{formatNumber(row.deliveredCookies)}</strong>
                    </div>
                    <div>
                      <span>Cost / bought cookie</span>
                      <strong>{row.purchaseTargetCookies ? `$${row.costPerPurchasedCookie.toFixed(3)}` : "N/A"}</strong>
                    </div>
                  </div>

                  <div className="ladder-plan">
                    <span>Purchase plan</span>
                    <strong>{planLabel(row.planCounts, eventConfig.cookiePacks)}</strong>
                  </div>

                  <div className="ladder-rewards">
                    <div>
                      <span>Guaranteed</span>
                      <p>{row.guaranteed.map(formatItem).join(", ")}</p>
                    </div>
                    <div>
                      <span>Bonus</span>
                      <p>{row.bonus.map(formatItem).join(", ")}</p>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>

          <section className="spending-events-section">
            <div className="spending-events-section-head">
              <h2>Bulk Cookies</h2>
            </div>
            <div className="cookie-pack-grid">
              {eventConfig.cookiePacks.map((pack) => (
                <article className="cookie-pack" key={pack.id}>
                  <strong>{formatNumber(pack.cookies)}</strong>
                  <span>cookies</span>
                  <div>{formatUsdCents(pack.priceCents)}</div>
                  <small>Limit {pack.limit}</small>
                </article>
              ))}
            </div>
          </section>
        </>
      ) : eventConfig.type === "battle-pass" ? (
        <section className="spending-events-section">
          <div className="spending-events-section-head">
            <h2>{selectedVariant?.theme || "Ladder"}</h2>
            <span>Free and premium rewards by pass level</span>
          </div>
          <div className="spending-ladder battle-pass-ladder">
            {battlePassRows.map((row) => (
              <article className="ladder-tier battle-pass-tier" key={row.level}>
                <div className="ladder-tier-top">
                  <div>
                    <span className="ladder-level">{`Level ${row.level}`}</span>
                    <strong>{`${formatNumber(row.points)} ${selectedVariant?.scoreLabel || "points"}`}</strong>
                  </div>
                  <span className="ladder-pill is-paid">{formatUsdCents(row.cumulativeCostCents)}</span>
                </div>

                <div className="ladder-stats">
                  <div>
                    <span>Premium cost</span>
                    <strong>{formatUsdCents(row.cumulativeCostCents)}</strong>
                  </div>
                  <div>
                    <span>Cost / unlocked level</span>
                    <strong>{formatUsdCents(row.costPerLevel)}</strong>
                  </div>
                </div>

                <div className="ladder-rewards battle-pass-rewards">
                  <div>
                    <span>Free</span>
                    <p>{row.free.map(formatItem).join(", ")}</p>
                  </div>
                  <div>
                    <span>Premium</span>
                    <p>{row.premium.map(formatItem).join(", ")}</p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>
      ) : (
        <ComplexSpendingEvent eventConfig={eventConfig} />
      )}

      <section className="spending-events-section reward-section">
        <div className="spending-events-section-head">
          <h2>Items</h2>
          <span>{eventConfig.type === "battle-pass" ? "All free and premium pass rewards" : "All configured rewards"}</span>
        </div>

        <div className="reward-layout">
          <div className="reward-panel">
            <h3>{eventConfig.type === "battle-pass" ? "Reward Totals" : eventConfig.type === "complex-event" ? "Configured Reward Totals" : "Ladder Totals"}</h3>
            <div className="reward-chip-grid">
              {rewardTotals.map((reward) => (
                <span className="reward-chip" key={`${reward.gid}-${reward.level || 0}`}>
                  {formatItem(reward)}
                </span>
              ))}
            </div>
          </div>

          {(eventConfig.feedRewardPools || []).map((pool) => {
            const totalWeight = pool.rewards.reduce((sum, reward) => sum + reward.weight, 0);
            return (
              <div className="reward-panel" key={pool.label}>
                <h3>{pool.label}</h3>
                <div className="feed-pool-list">
                  {pool.rewards.map((reward) => (
                    <div className="feed-pool-row" key={`${pool.label}-${reward.gid}-${reward.level || 0}`}>
                      <span>{formatItem(reward)}</span>
                      <strong>{((reward.weight / totalWeight) * 100).toFixed(1)}%</strong>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </main>
  );
}

export default SpendingEvents;
